/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useDispatch} from "useDispatch";
import CookiesModal from "components/Cookies/components/CookiesModal";
import {openModal} from "helpers/actions";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import Loader from "components/Loader";
import BookingAction from "store/booking/BookingAction";
import {Cookies_status} from "types/enums/Cookies_status";

interface Props {}

const Cookies = (props: Props) => {
    const dispatch = useDispatch();
    const openCookiesModal = () => dispatch(openModal(<CookiesModal />, {}));
    const {t} = useTranslation();
    const hall = useTypedSelector(state => state.hallState.hall);
    const cookies = useTypedSelector(state => state.bookingState.cookies);

    if (!hall) {
        return <Loader />;
    }

    const agreeCookie = () => {
        dispatch(
            BookingAction.setCookies({
                ...cookies,
                analytics_cookies: true,
                advert_cookies: true,
                status: Cookies_status.AGREED,
            })
        );
    };

    const disagreeCookie = () => {
        dispatch(
            BookingAction.setCookies({
                ...cookies,
                analytics_cookies: true,
                advert_cookies: false,
                status: Cookies_status.AGREED,
            })
        );
    };

    return (
        <div className="Cookies z-40 inset-x-0 bottom-0 sm:bottom-0 bg-white fixed inset-x-0 p-4 overscroll-auto">
            <div className={"border-t border-gray-800 relative z-40 pt-4"}>
                <div className={"max-w-5xl xl:max-w-7xl z-40 m-auto relative "}>
                    <div className="flex flex-wrap justify-center z-40 relative">
                        <span>Cookies 🍪</span>
                    </div>
                    <p className={"mt-3 mb-4 z-40 text-sm md:text-base"}>
                        {t("Web")} <span className={"decline-link"}>{hall.domain}</span>{" "}
                        <b>
                            {t(
                                "uses cookies and other technologies to ensure your safety and your own reliability, monitor its performance and provide personalized services"
                            )}
                        </b>
                        . {t("To this end, we collect information about users their behavior and devices")}. {t("By clicking")}
                        <b> “OK” </b>,{" "}
                        {t("you agree provided that we store this information and, if necessary, share it with third parties, i.e. our business partners.")}{" "}
                        {t("If you reject this, we will only use basic cookies.")} {t("Select 'Edit Settings' for details and custom settings.")}{" "}
                        {t("You can adjust your preferences at any time by deleting the cookies in your browser. See for more information about our")}{" "}
                        <span className={"decline-link"}>{t("GDPR about personal information")}</span>.
                    </p>
                    <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row justify-center sm:space-x-8 z-40 mb-8">
                        <button className={"border-primary-800/50 border px-4 py-2 rounded-full text-primary-800"} onClick={disagreeCookie}>
                            {t("Turn off tracking cookies")}
                        </button>
                        <button className={"bg-cta-800 text-contrast-800 px-6 py-5 rounded-full"} onClick={agreeCookie}>
                            {t("Accept cookies")}
                        </button>
                    </div>
                    <p className={"mt-4"}>
                        {t("You can edit settings")}{" "}
                        <button className={"underline decoration-2 underline-offset-2 decoration-blue-600"} type={"button"} onClick={openCookiesModal}>
                            {t("here")}
                        </button>
                        .
                    </p>
                </div>
            </div>
            <div className={"fixed inset-0 w-full h-full backdrop-blur-[2px] z-30"} />
        </div>
    );
};

export default Cookies;
